import React, { useMemo, useRef, useState, useEffect } from "react";
import Spline from "@splinetool/react-spline";
import { Header } from "../components/site/Header";
import { CalendlyModal } from "../components/site/CalendlyModal";
import { servicesGroups, hostingFeatures, testimonials, processSteps, faqs } from "../components/mock/mock";
import { Button } from "../components/ui/button.jsx";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card.jsx";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion.jsx";
import { Input } from "../components/ui/input.jsx";
import { Textarea } from "../components/ui/textarea.jsx";
import { CheckCircle2, Rocket, Shield, Zap, ArrowRight, LineChart, Layers, Cpu, Globe, Phone } from "lucide-react";

const Accent = ({ children }) => (
  <span className="text-[#00FFD1]">{children}</span>
);

const Section = ({ id, children, className = "" }) => (
  <section id={id} className={`relative w-full bg-black ${className}`}>
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8">{children}</div>
  </section>
);

const FeaturePill = ({ text }) => (
  <div className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-white/70 text-sm">{text}</div>
);

export default function Landing() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });

  useEffect(() => {
    const saved = localStorage.getItem("skyrise_lead");
    if (saved) setForm(JSON.parse(saved));
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      if (!backend) throw new Error("Missing backend URL");
      const res = await fetch(`${backend}/api/leads/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, source: "contact_form" }),
      });
      if (!res.ok) throw new Error("Failed to submit lead");
      console.log("Thanks! We'll reach out shortly.");
    } catch (err) {
      console.error(err);
      // Fallback to local save to avoid loss
      localStorage.setItem("skyrise_lead", JSON.stringify(form));
      console.log("Network issue. Saved locally; we'll retry soon.");
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />

      {/* Hero */}
      <Section id="home" className="pt-24 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div className="space-y-6">
            <h1 className="text-5xl md:text-6xl font-semibold tracking-tight">Build, launch, and scale with <Accent>SKYRISE</Accent></h1>
            <p className="text-white/70 text-lg max-w-xl">Full‑stack digital solutions: from brand and product to hosting and automation. We ship fast, craft beautifully, and optimize for growth.</p>
            <div className="flex flex-wrap items-center gap-3">
              <CalendlyModal trigger={({onClick}) => (<button onClick={onClick} className="btn-primary dark-button-animate"><ArrowRight size={18} /> Book a Call</button>)} />
              <a href="#work" className="btn-secondary">View Work</a>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {["Websites", "Apps", "ERP", "AI", "Brand", "SEO", "Hosting"].map((t) => (
                <FeaturePill key={t} text={t} />
              ))}
            </div>
          </div>

          <div className="relative min-h-[420px] lg:min-h-[560px]">
            <div className="absolute inset-0 rounded-xl bg-white/5 border border-white/10 backdrop-blur-2xl" />
            <div className="relative z-10 h-[420px] lg:h-[560px] overflow-visible">
              <Spline scene="https://prod.spline.design/NbVmy6DPLhY-5Lvg/scene.splinecode" />
            </div>
          </div>
        </div>
      </Section>

      {/* Services Overview */}
      <Section id="services" className="py-16">
        <div className="flex items-end justify-between mb-6">
          <h2 className="text-3xl md:text-4xl font-semibold">What we do</h2>
          <a href="#contact" className="text-[#00FFD1] hover:opacity-80 transition">Get a custom proposal</a>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {servicesGroups.map((group) => (
            <Card key={group.title} className="bg-black/60 border-white/10 hover:border-white/20 transition">
              <CardHeader>
                <CardTitle className="text-white/90">{group.title}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {group.items.map((it) => (
                  <div key={it.label} className="flex items-start gap-2">
                    <CheckCircle2 size={18} className="text-[#00FFD1] mt-0.5" />
                    <div>
                      <p className="text-white/90 text-sm">{it.label}</p>
                      {it.desc && <p className="text-white/50 text-xs">{it.desc}</p>}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      </Section>

      {/* Hosting */}
      <Section id="hosting" className="py-16">
        <div className="flex items-end justify-between mb-6">
          <h2 className="text-3xl md:text-4xl font-semibold">Hosting, without headaches</h2>
          <a href="#contact" className="text-[#00FFD1] hover:opacity-80 transition">Talk to an engineer</a>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="bg-black/60 border-white/10 lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-white/90">Enterprise-grade foundations</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {hostingFeatures.map((f) => (
                <div key={f.label} className="flex items-center gap-2">
                  <Shield size={18} className="text-[#00FFD1]" />
                  <span className="text-white/80 text-sm">{f.label}</span>
                </div>
              ))}
            </CardContent>
          </Card>
          <Card className="bg-black/60 border-white/10">
            <CardHeader>
              <CardTitle className="text-white/90">Popular tiers</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {["Shared Hosting", "VPS", "Dedicated", "Cloud", "WordPress", "E‑Commerce"].map((t) => (
                <div key={t} className="flex items-center justify-between py-2 border-b border-white/10 last:border-b-0">
                  <span className="text-white/80 text-sm">{t}</span>
                  <a href="#contact" className="text-[#00FFD1] text-sm">Get Quote</a>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </Section>

      {/* Process */}
      <Section id="process" className="py-16">
        <div className="flex items-end justify-between mb-6">
          <h2 className="text-3xl md:text-4xl font-semibold">How we work</h2>
          <span className="text-white/60 text-sm">Transparent, iterative, outcomes‑driven</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {processSteps.map((s, i) => (
            <div key={s.title} className="p-4 rounded-lg bg-white/5 border border-white/10">
              <div className="text-white/60 text-xs mb-2">Step {i + 1}</div>
              <div className="text-white font-medium mb-1">{s.title}</div>
              <div className="text-white/60 text-sm">{s.desc}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* Testimonials / Work */}
      <Section id="work" className="py-16">
        <div className="flex items-end justify-between mb-6">
          <h2 className="text-3xl md:text-4xl font-semibold">Results our clients feel</h2>
          <a href="#contact" className="text-[#00FFD1]">Start your project</a>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {testimonials.map((t) => (
            <Card key={t.name} className="bg-black/60 border-white/10">
              <CardContent className="p-6">
                <p className="text-white/80 mb-4">“{t.quote}”</p>
                <p className="text-white/60 text-sm">{t.name} • {t.role}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </Section>

      {/* FAQ */}
      <Section id="faq" className="py-16">
        <div className="flex items-end justify-between mb-6">
          <h2 className="text-3xl md:text-4xl font-semibold">Frequently asked</h2>
          <span className="text-white/60 text-sm">Quick answers</span>
        </div>
        <Accordion type="single" collapsible className="bg-black/60 border border-white/10 rounded-lg">
          {faqs.map((f, idx) => (
            <AccordionItem key={idx} value={`item-${idx}`}>
              <AccordionTrigger className="px-4 text-white/90">{f.q}</AccordionTrigger>
              <AccordionContent className="px-4 text-white/70">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Section>

      {/* Contact CTA */}
      <Section id="contact" className="py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          <div className="lg:col-span-2 p-6 rounded-xl bg-white/5 border border-white/10 backdrop-blur-xl">
            <h3 className="text-2xl font-semibold mb-4">Book a call</h3>
            <p className="text-white/70 mb-6">Tell us about your project. We will reply within 24 hours.</p>
            <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-white/70">Name</label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-black/60 border-white/20 text-white" required />
              </div>
              <div>
                <label className="text-sm text-white/70">Email</label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-black/60 border-white/20 text-white" required />
              </div>
              <div className="md:col-span-2">
                <label className="text-sm text-white/70">Message</label>
                <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="bg-black/60 border-white/20 text-white min-h-[120px]" placeholder="Project scope, timelines, goals" />
              </div>
              <div className="md:col-span-2 flex items-center gap-3">
                <button type="submit" className="btn-primary dark-button-animate"><ArrowRight size={18} /> Submit</button>
                <button type="button" onClick={() => console.log("We also respond on LinkedIn") } className="btn-secondary">Contact options</button>
              </div>
            </form>
          </div>
          <div className="p-6 rounded-xl bg-white/5 border border-white/10">
            <h4 className="text-white/90 mb-2">Why SKYRISE?</h4>
            <ul className="space-y-2 text-white/70 text-sm">
              <li className="flex items-center gap-2"><Zap size={16} className="text-[#00FFD1]" /> Fast iterations, clear comms</li>
              <li className="flex items-center gap-2"><Shield size={16} className="text-[#00FFD1]" /> Secure by default</li>
              <li className="flex items-center gap-2"><LineChart size={16} className="text-[#00FFD1]" /> Growth-focused delivery</li>
              <li className="flex items-center gap-2"><Globe size={16} className="text-[#00FFD1]" /> Global-ready hosting</li>
            </ul>
          </div>
        </div>
        <div className="text-center text-white/50 text-xs mt-8">© {new Date().getFullYear()} SKYRISE. All rights reserved.</div>
      </Section>

    </div>
  );
}